import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useGlobal } from '../Context/GlobalContext';
import { useAuth } from '../Auth/AuthContext';

const DEFAULT_PROVIDERS = ['Google', 'OpenAI', 'Anthropic', 'xAI', 'Image', 'Video'];

const AIModelsContext = createContext();

export const AIModelsProvider = ({ children }) => {
    const { apiUrl } = useGlobal();
    const { getToken, isLoggedIn } = useAuth();

    const [models, setModels] = useState({});
    const [providers, setProviders] = useState(DEFAULT_PROVIDERS);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastFetched, setLastFetched] = useState(null);

    // Fetch models from API
    const fetchModels = useCallback(async () => {
        if (!isLoggedIn || !apiUrl) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const token = getToken();

            const response = await fetch(`${apiUrl}/api/v1/apps/ai-models`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch AI models');
            }

            const data = await response.json();

            if (data.success && data.models && Object.keys(data.models).length > 0) {
                setModels(data.models);
                setProviders(data.providers || DEFAULT_PROVIDERS);
            } else {
                setError('No AI models configured');
            }

            setLastFetched(new Date());
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [apiUrl, getToken, isLoggedIn]);

    // Fetch models on mount and when authentication changes
    useEffect(() => {
        if (isLoggedIn) {
            fetchModels();
        }
    }, [isLoggedIn, fetchModels]);

    // Refresh models (can be called manually)
    const refreshModels = useCallback(() => {
        return fetchModels();
    }, [fetchModels]);

    // Get model display name by ID
    const getModelDisplayName = useCallback((modelId) => {
        if (!modelId || !models) return 'Unknown Model';
        const model = models[modelId];
        return model?.displayName || modelId;
    }, [models]);

    // Get model credits by ID
    const getModelCredits = useCallback((modelId) => {
        if (!modelId || !models) return 1;
        const model = models[modelId];
        return model?.credits || 1;
    }, [models]);

    // Get model config by ID
    const getModelConfig = useCallback((modelId) => {
        if (!modelId || !models) return null;
        return models[modelId] || null;
    }, [models]);

    // Get models by provider
    const getModelsByProvider = useCallback((provider) => {
        if (!provider || !models) return [];
        return Object.values(models).filter(m => m.provider === provider);
    }, [models]);

    // Get models by category
    const getModelsByCategory = useCallback((category) => {
        if (!models) return [];
        if (!category) return Object.values(models);
        return Object.values(models).filter(m => m.category === category);
    }, [models]);

    // Get text models (most common use case)
    const getTextModels = useCallback(() => {
        return getModelsByCategory('text');
    }, [getModelsByCategory]);

    // Get image models
    const getImageModels = useCallback(() => {
        return getModelsByCategory('image');
    }, [getModelsByCategory]);

    // Get video models
    const getVideoModels = useCallback(() => {
        return getModelsByCategory('video');
    }, [getModelsByCategory]);

    // Get models grouped by provider (for dropdowns)
    const getModelsGroupedByProvider = useCallback(() => {
        if (!models) return {};
        const grouped = {};
        for (const [, model] of Object.entries(models)) {
            const provider = model.provider || 'Other';
            if (!grouped[provider]) {
                grouped[provider] = [];
            }
            grouped[provider].push(model);
        }
        return grouped;
    }, [models]);

    // Get provider icon name
    const getProviderIcon = useCallback((provider) => {
        const iconMap = {
            'Google': 'google',
            'OpenAI': 'openai',
            'Anthropic': 'anthropic',
            'xAI': 'xai',
            'Image': 'image',
            'Video': 'video'
        };
        return iconMap[provider] || 'default';
    }, []);

    // Convert to model structure format used by existing components (AISettings, etc.)
    const getModelStructure = useCallback(() => {
        const grouped = getModelsGroupedByProvider();
        return Object.entries(grouped).map(([provider, providerModels]) => ({
            id: provider.toLowerCase(),
            name: provider,
            icon: getProviderIcon(provider),
            color: getProviderColor(provider),
            models: providerModels.map(m => ({
                id: m.id,
                name: m.displayName,
                credits: m.credits,
                descriptions: m.descriptions || {}  // Include descriptions for dynamic i18n
            }))
        }));
    }, [getModelsGroupedByProvider, getProviderIcon]);

    // Get all models as array
    const getAllModels = useCallback(() => {
        if (!models) return [];
        return Object.values(models);
    }, [models]);

    // Get all model IDs
    const getAllModelIds = useCallback(() => {
        if (!models) return [];
        return Object.keys(models);
    }, [models]);

    // Check if models are ready (loaded and not empty)
    const isReady = !loading && Object.keys(models).length > 0;

    const value = {
        // Data
        models,
        providers,
        loading,
        error,
        lastFetched,
        isReady,

        // Actions
        refreshModels,

        // Getters
        getModelDisplayName,
        getModelCredits,
        getModelConfig,
        getModelsByProvider,
        getModelsByCategory,
        getTextModels,
        getImageModels,
        getVideoModels,
        getModelsGroupedByProvider,
        getModelStructure,
        getAllModels,
        getAllModelIds,
        getProviderIcon,
    };

    return (
        <AIModelsContext.Provider value={value}>
            {children}
        </AIModelsContext.Provider>
    );
};

// Helper function to get provider color
const getProviderColor = (provider) => {
    const colorMap = {
        'Google': '#4285F4',
        'OpenAI': '#10A37F',
        'Anthropic': '#CC785C',
        'xAI': '#000000',
        'Image': '#8B5CF6',
        'Video': '#06B6D4'
    };
    return colorMap[provider] || '#6B7280';
};

export const useAIModels = () => {
    const context = useContext(AIModelsContext);
    if (!context) {
        throw new Error('useAIModels must be used within an AIModelsProvider');
    }
    return context;
};

export default AIModelsContext;
